import { format } from "date-fns";

import { DEFAULT_TABLE_SORT_BY_CREATED_AT } from "@/utils/global";
import {
  createSingleSelectOptionDraft,
  getStatusColor,
  nextSingleSelectColor,
  resolveSingleSelectColorFromHex,
  singleSelectColorToHex,
} from "@/utils/data-table-single-select-colors";

export const DATA_TABLES_PAGE_LIMIT = 10;

export const DATA_TABLE_WORKBOOKS_PAGE_LIMIT = 30;

export const DATA_TABLE_ROWS_PAGE_LIMIT = 30;

export const MAX_DATA_TABLE_IMPORT_ROWS = 10000;

/** List page column — matches prototype `max-w-[1080px]`. */
export const DATA_TABLES_PAGE_COLUMN_CLASS = "mx-auto w-full max-w-[1080px]";

/** Outline toolbar actions (Import / Export / Filter). */
export const DATA_TABLE_TOOLBAR_OUTLINE_BUTTON_CLASS =
  "rounded-full border-border bg-background font-semibold text-deepPurple shadow-none hover:border-primary hover:bg-primary/5 hover:text-primary dark:text-foreground dark:hover:text-primary [&_svg]:text-current";

/** Outline buttons in the grid empty state (Upload / Import from CRM). */
export const DATA_TABLE_EMPTY_STATE_BUTTON_CLASS = `${DATA_TABLE_TOOLBAR_OUTLINE_BUTTON_CLASS} h-8 gap-1.5 px-3.5 text-xs [&_svg]:size-3.5`;

/** Prototype accent tokens — lavender hover bg + deep purple text (AIOS-prototype). */
export const DATA_TABLE_PROTOTYPE_ACCENT_SCOPE_CLASS =
  "[--accent:258_42%_94%] [--accent-foreground:245_64%_35%] dark:[--accent:0_0%_14.9%] dark:[--accent-foreground:0_0%_98%]";

/** Radix pointer hover uses data-highlighted; keep icons synced with text. */
export const DATA_TABLE_PROTOTYPE_ACCENT_ITEM_CLASS =
  "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground [&_svg]:text-current";

/** Column header dropdown — prototype accent tokens (lavender bg + deep purple text). */
export const DATA_TABLE_COLUMN_MENU_CONTENT_CLASS = `w-[220px] py-1.5 ${DATA_TABLE_PROTOTYPE_ACCENT_SCOPE_CLASS}`;

/** Column header dropdown item hover. */
export const DATA_TABLE_COLUMN_MENU_ITEM_CLASS = `cursor-pointer ${DATA_TABLE_PROTOTYPE_ACCENT_ITEM_CLASS}`;

/** Select dropdowns in data tables (e.g. connected worker data access). */
export const DATA_TABLE_SELECT_CONTENT_CLASS =
  DATA_TABLE_PROTOTYPE_ACCENT_SCOPE_CLASS;

/** Select item hover inside data table accent-scoped menus. */
export const DATA_TABLE_SELECT_ITEM_CLASS = DATA_TABLE_PROTOTYPE_ACCENT_ITEM_CLASS;

/** Shared shell for data table create/edit dialogs (prototype v2). */
export const DATA_TABLE_DIALOG_CONTENT_CLASS =
  "flex max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-lg flex-col gap-0 overflow-hidden rounded-[32px] border-border bg-background p-5 shadow-xl sm:rounded-[32px] sm:p-6 [&>button:last-child]:hidden";

export const DEFAULT_DATA_TABLE_SORT = {
  sortBy: DEFAULT_TABLE_SORT_BY_CREATED_AT.sortBy as DataTableSortField,
  sortOrder: DEFAULT_TABLE_SORT_BY_CREATED_AT.sortOrder,
};

export const DEFAULT_UNGROUPED_DATA_TABLE_SORT = {
  sortBy: "last_updated_at" as DataTableSortField,
  sortOrder: "desc" as const,
};

export function buildDataTablesQuery(params: {
  page: number;
  search: string;
  sortBy: DataTableSortField;
  sortOrder: "asc" | "desc";
  groupingStatus?: DataTableGroupingStatus;
  workbookId?: string;
  limit?: number;
}): string {
  const searchParams = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit ?? DATA_TABLES_PAGE_LIMIT),
    sort_by: params.sortBy,
    sort_order: params.sortOrder,
  });

  if (params.search.trim()) {
    searchParams.set("search", params.search.trim());
  }

  if (params.groupingStatus) {
    searchParams.set("grouping_status", params.groupingStatus);
  }

  if (params.workbookId) {
    searchParams.set("workbook_id", params.workbookId);
  }

  return `/api/data-tables?${searchParams.toString()}`;
}

export function buildDataTableWorkbooksQuery(params: {
  page: number;
  search: string;
  sortBy?: DataTableWorkbookSortField;
  sortOrder?: "asc" | "desc";
  limit?: number;
}): string {
  const searchParams = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit ?? DATA_TABLE_WORKBOOKS_PAGE_LIMIT),
    sort_by: params.sortBy ?? "updated_at",
    sort_order: params.sortOrder ?? "desc",
  });

  if (params.search.trim()) {
    searchParams.set("search", params.search.trim());
  }

  return `/api/data-table-workbooks?${searchParams.toString()}`;
}

export function buildDataTableWorkbookPath(workbookId: string): string {
  return `/api/data-table-workbooks/${workbookId}`;
}

export function buildDataTableWorkbookTablesPath(workbookId: string): string {
  return `/api/data-table-workbooks/${workbookId}/data-tables`;
}

export function buildDataTableWorkbookTablePath(
  workbookId: string,
  dataTableId: string
): string {
  return `/api/data-table-workbooks/${workbookId}/data-tables/${dataTableId}`;
}

export function getDataTableWorkbookTotalRows(
  workbook: DataTableWorkbook
): number {
  return workbook.data_tables.reduce((total, table) => total + table.row_count, 0);
}

export function formatDataTableCountLabel(
  count: number,
  singular: string,
  plural = `${singular}s`
): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Short display id for the page header (e.g. tbl_results). Copy still uses the real UUID. */
export function formatDataTableShortId(
  name: string,
  fallbackId?: string
): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
  const compactName = slug.split("_").filter(Boolean).slice(0, 2).join("_");

  if (compactName) {
    return `tbl_${compactName}`;
  }

  const compactId = (fallbackId ?? "").replace(/-/g, "").slice(0, 8);
  return `tbl_${compactId || "table"}`;
}

export function formatDataTableRelativeTime(dateString: string): string {
  const diffMs = Date.now() - new Date(dateString).getTime();
  if (Number.isNaN(diffMs) || diffMs < 60_000) {
    return "just now";
  }

  const diffM = Math.floor(diffMs / 60_000);
  if (diffM < 60) {
    return `${diffM}m ago`;
  }

  const diffH = Math.floor(diffMs / 3_600_000);
  if (diffH < 24) {
    return `${diffH}h ago`;
  }

  const diffD = Math.floor(diffMs / 86_400_000);
  if (diffD < 7) {
    return `${diffD}d ago`;
  }

  return format(new Date(dateString), "MMM d, yyyy");
}

export function getDataTableOwnerInitials(fullname: string): string {
  return fullname
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export const DATA_TABLE_COLUMN_DATA_TYPE_OPTIONS: {
  value: DataTableColumnDataType;
  label: string;
}[] = [
  { value: "TEXT", label: "Text" },
  { value: "NUMBER", label: "Number" },
  { value: "DATE", label: "Date" },
  { value: "TIME", label: "Time" },
  { value: "TIMESTAMP", label: "Timestamp" },
  { value: "BOOLEAN", label: "Boolean" },
  { value: "CURRENCY", label: "Currency" },
  { value: "EMAIL", label: "Email" },
  { value: "PHONE", label: "Phone" },
  { value: "URL", label: "URL" },
  { value: "UUID", label: "UUID" },
  { value: "JSON", label: "JSON" },
  { value: "SINGLE_SELECT", label: "Single Select" },
];

const TEXT_LIKE_FILTER_OPERATORS: DataTableColumnFilterOperator[] = [
  "contains",
  "doesNotContain",
  "is",
  "isNot",
  "isEmpty",
  "isNotEmpty",
];

const SINGLE_SELECT_FILTER_OPERATORS: DataTableColumnFilterOperator[] = [
  "is",
  "isNot",
  "isEmpty",
  "isNotEmpty",
];

const NUMBER_FILTER_OPERATORS: DataTableColumnFilterOperator[] = [
  "=",
  "!=",
  ">",
  ">=",
  "<",
  "<=",
  "isEmpty",
  "isNotEmpty",
];

const DATE_FILTER_OPERATORS: DataTableColumnFilterOperator[] = [
  "is",
  "isNot",
  "isBefore",
  "isAfter",
  "isOnOrBefore",
  "isOnOrAfter",
  "isWithin",
  "isEmpty",
  "isNotEmpty",
];

const BOOLEAN_FILTER_OPERATORS: DataTableColumnFilterOperator[] = [
  "isTrue",
  "isFalse",
  "isEmpty",
  "isNotEmpty",
];

export const DATA_TABLE_FILTER_VALUELESS_OPERATORS = new Set<DataTableColumnFilterOperator>([
  "isEmpty",
  "isNotEmpty",
  "isTrue",
  "isFalse",
]);

export const DATA_TABLE_FILTER_OPERATOR_LABELS: Record<
  DataTableColumnFilterOperator,
  string
> = {
  contains: "contains",
  doesNotContain: "does not contain",
  is: "is",
  isNot: "is not",
  isEmpty: "is empty",
  isNotEmpty: "is not empty",
  "=": "equals",
  "!=": "not equals",
  ">": "greater than",
  ">=": "greater than or equal",
  "<": "less than",
  "<=": "less than or equal",
  isBefore: "is before",
  isAfter: "is after",
  isOnOrBefore: "is on or before",
  isOnOrAfter: "is on or after",
  isWithin: "is within",
  isTrue: "is true",
  isFalse: "is false",
};

export function getDataTableFilterOperatorsForType(
  dataType: DataTableColumnDataType
): DataTableColumnFilterOperator[] {
  switch (dataType) {
    case "NUMBER":
    case "CURRENCY":
      return NUMBER_FILTER_OPERATORS;
    case "DATE":
    case "TIME":
    case "TIMESTAMP":
      return DATE_FILTER_OPERATORS;
    case "BOOLEAN":
      return BOOLEAN_FILTER_OPERATORS;
    case "SINGLE_SELECT":
      return SINGLE_SELECT_FILTER_OPERATORS;
    default:
      return TEXT_LIKE_FILTER_OPERATORS;
  }
}

export function isDataTableFilterOperatorValueless(
  operator: DataTableColumnFilterOperator | ""
): boolean {
  return (
    Boolean(operator) &&
    DATA_TABLE_FILTER_VALUELESS_OPERATORS.has(
      operator as DataTableColumnFilterOperator
    )
  );
}

export function serializeDataTableRowFilters(
  filters: DataTableColumnFilter[]
): string | null {
  const validFilters = filters.filter((filter) => {
    if (!filter.column_id || !filter.operator) return false;
    if (isDataTableFilterOperatorValueless(filter.operator)) return true;
    if (filter.operator === "isWithin") {
      return (
        Array.isArray(filter.value) &&
        Boolean(filter.value[0]?.trim()) &&
        Boolean(filter.value[1]?.trim())
      );
    }
    if (typeof filter.value === "string") {
      return filter.value.trim() !== "";
    }
    return filter.value !== null && filter.value !== undefined;
  });

  const payload = validFilters.map((filter, index) => {
    let value: string | null = null;

    if (!isDataTableFilterOperatorValueless(filter.operator)) {
      if (filter.operator === "isWithin" && Array.isArray(filter.value)) {
        value = `${filter.value[0].trim()},${filter.value[1].trim()}`;
      } else if (typeof filter.value === "string") {
        value = filter.value.trim();
      }
    }

    const item: {
      column_id: string;
      operator: DataTableColumnFilterOperator | "";
      value: string | null;
      next_conjunction?: DataTableFilterConjunction;
    } = {
      column_id: filter.column_id,
      operator: filter.operator,
      value,
    };

    if (index < validFilters.length - 1) {
      item.next_conjunction = filter.next_conjunction ?? "AND";
    }

    return item;
  });

  return payload.length > 0 ? JSON.stringify(payload) : null;
}

const DEFAULT_COLUMN_TYPE_STYLE = {
  label: "text",
  color:
    "border-blue-100 bg-blue-50 font-normal text-blue-600 dark:border-blue-900/40 dark:bg-blue-950/20 dark:text-blue-400",
  bar: "bg-blue-400",
};

export const DATA_TABLE_COLUMN_TYPE_STYLES: Record<
  DataTableColumnDataType,
  { label: string; color: string; bar: string }
> = {
  TEXT: DEFAULT_COLUMN_TYPE_STYLE,
  NUMBER: {
    label: "number",
    color:
      "border-green-100 bg-green-50 font-normal text-green-600 dark:border-green-900/40 dark:bg-green-950/20 dark:text-green-400",
    bar: "bg-green-400",
  },
  DATE: {
    label: "date",
    color:
      "border-orange-100 bg-orange-50 font-normal text-orange-600 dark:border-orange-900/40 dark:bg-orange-950/20 dark:text-orange-400",
    bar: "bg-orange-400",
  },
  TIME: {
    label: "time",
    color:
      "border-indigo-100 bg-indigo-50 font-normal text-indigo-600 dark:border-indigo-900/40 dark:bg-indigo-950/20 dark:text-indigo-400",
    bar: "bg-indigo-400",
  },
  TIMESTAMP: {
    label: "timestamp",
    color:
      "border-red-100 bg-red-50 font-normal text-red-600 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400",
    bar: "bg-red-400",
  },
  BOOLEAN: {
    label: "boolean",
    color:
      "border-violet-100 bg-violet-50 font-normal text-violet-600 dark:border-violet-900/40 dark:bg-violet-950/20 dark:text-violet-400",
    bar: "bg-violet-400",
  },
  CURRENCY: {
    label: "currency",
    color:
      "border-emerald-100 bg-emerald-50 font-normal text-emerald-600 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-400",
    bar: "bg-emerald-400",
  },
  EMAIL: {
    label: "email",
    color:
      "border-sky-100 bg-sky-50 font-normal text-sky-600 dark:border-sky-900/40 dark:bg-sky-950/20 dark:text-sky-400",
    bar: "bg-sky-400",
  },
  PHONE: {
    label: "phone",
    color:
      "border-teal-100 bg-teal-50 font-normal text-teal-600 dark:border-teal-900/40 dark:bg-teal-950/20 dark:text-teal-400",
    bar: "bg-teal-400",
  },
  URL: {
    label: "url",
    color:
      "border-cyan-100 bg-cyan-50 font-normal text-cyan-600 dark:border-cyan-900/40 dark:bg-cyan-950/20 dark:text-cyan-400",
    bar: "bg-cyan-400",
  },
  UUID: {
    label: "uuid",
    color:
      "border-violet-100 bg-violet-50 font-normal text-violet-600 dark:border-violet-900/40 dark:bg-violet-950/20 dark:text-violet-400",
    bar: "bg-violet-400",
  },
  JSON: {
    label: "json",
    color:
      "border-amber-100 bg-amber-50 font-normal text-amber-600 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-400",
    bar: "bg-amber-400",
  },
  SINGLE_SELECT: {
    label: "single select",
    color:
      "border-fuchsia-100 bg-fuchsia-50 font-normal text-fuchsia-600 dark:border-fuchsia-900/40 dark:bg-fuchsia-950/20 dark:text-fuchsia-400",
    bar: "bg-fuchsia-400",
  },
};

export function getDataTableColumnTypeStyle(
  dataType: DataTableColumnDataType
): { label: string; color: string; bar: string } {
  return DATA_TABLE_COLUMN_TYPE_STYLES[dataType] ?? DATA_TABLE_COLUMN_TYPE_STYLES.TEXT;
}

export function getDataTableColumnTypeIconClass(
  dataType: DataTableColumnDataType
): string {
  const colorClasses = getDataTableColumnTypeStyle(dataType).color.split(" ");
  return (
    colorClasses
      .filter((className) => className.startsWith("text-"))
      .join(" ") || "text-muted-foreground"
  );
}

export function buildDataTableColumnsPath(dataTableId: string): string {
  return `/api/${dataTableId}/columns`;
}

export function buildDataTableColumnsSchemaPath(dataTableId: string): string {
  return `/api/${dataTableId}/columns/schema`;
}

export function buildDataTableColumnPath(
  dataTableId: string,
  columnId: string
): string {
  return `/api/${dataTableId}/columns/${columnId}`;
}

export function buildDataTableRowsPath(dataTableId: string): string {
  return `/api/${dataTableId}/rows`;
}

export function buildDataTableRowsQuery(
  dataTableId: string,
  options?: {
    page?: number;
    cursor?: string | null;
    search?: string;
    limit?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    filters?: string | null;
  }
): string {
  const params = new URLSearchParams();

  params.set("limit", String(options?.limit ?? DATA_TABLE_ROWS_PAGE_LIMIT));
  params.set("sort_by", options?.sortBy ?? "created_at");
  params.set("sort_order", options?.sortOrder ?? "desc");

  // Cursor and page are mutually exclusive — prefer cursor when present.
  if (options?.cursor) {
    params.set("cursor", options.cursor);
  } else if (options?.page && options.page >= 1) {
    params.set("page", String(options.page));
  }

  if (options?.search?.trim()) {
    params.set("search", options.search.trim());
  }

  if (options?.filters?.trim()) {
    params.set("filters", options.filters.trim());
  }

  const query = params.toString();
  return `${buildDataTableRowsPath(dataTableId)}?${query}`;
}

/** Adjacent page steps (±1) use cursor when available; larger jumps use page. */
export function resolveDataTableRowsPagination(params: {
  fromPage: number;
  toPage: number;
  nextCursor: string | null | undefined;
  cursorsByPage: Record<number, string | undefined>;
}): { page?: number; cursor?: string } {
  const { fromPage, toPage, nextCursor, cursorsByPage } = params;
  const step = toPage - fromPage;

  if (Math.abs(step) === 1) {
    if (step === 1 && nextCursor) {
      return { cursor: nextCursor };
    }

    if (step === -1) {
      if (toPage <= 1) {
        return { page: 1 };
      }

      const previousCursor = cursorsByPage[toPage];
      if (previousCursor) {
        return { cursor: previousCursor };
      }
    }
  }

  return { page: toPage };
}

export function buildDataTableRowPath(
  dataTableId: string,
  rowId: string
): string {
  return `/api/${dataTableId}/rows/${rowId}`;
}

export function buildDataTableRowsBulkDeletePath(dataTableId: string): string {
  return `/api/${dataTableId}/rows/bulk`;
}

export function buildDataTableRowsExportPath(
  dataTableId: string,
  params?: {
    search?: string;
    sort_by?: string;
    sort_order?: "asc" | "desc";
    filters?: string;
  }
): string {
  const searchParams = new URLSearchParams();

  if (params?.search?.trim()) {
    searchParams.set("search", params.search.trim());
  }
  if (params?.sort_by) {
    searchParams.set("sort_by", params.sort_by);
  }
  if (params?.sort_order) {
    searchParams.set("sort_order", params.sort_order);
  }
  if (params?.filters) {
    searchParams.set("filters", params.filters);
  }

  const query = searchParams.toString();
  return `/api/${dataTableId}/rows/export${query ? `?${query}` : ""}`;
}

export function buildDataTableRowsBulkAddPath(dataTableId: string): string {
  return `/api/${dataTableId}/rows/bulk-add`;
}

export function buildDataTableCrmMappingPath(dataTableId: string): string {
  return `/api/data-tables/${dataTableId}/crm-mapping`;
}

export function buildDataTablePushToCrmPath(dataTableId: string): string {
  return `/api/data-tables/${dataTableId}/push-to-crm`;
}

export function buildDataTableImportFromCrmPath(dataTableId: string): string {
  return `/api/data-tables/${dataTableId}/import-from-crm`;
}

export function buildDataTableCrmStatsPath(dataTableId: string): string {
  return `/api/data-tables/${dataTableId}/crm-stats`;
}

export function buildDataTableConnectedWorkersPath(
  dataTableId: string
): string {
  return `/api/data-tables/${dataTableId}/connected-workers`;
}

export function buildDataTableColumnTaskBindingsPath(
  dataTableId: string
): string {
  return `/api/data-tables/${dataTableId}/column-task-bindings`;
}

export function buildDataTableColumnTaskBindingPath(
  dataTableId: string,
  bindingId: string
): string {
  return `/api/data-tables/${dataTableId}/column-task-bindings/${bindingId}`;
}

export const DATA_TABLE_COLUMN_TASK_FIRE_ON_OPTIONS: {
  value: DataTableColumnTaskFireOn;
  label: string;
}[] = [
  { value: "ON_CREATE", label: "Created (first value only)" },
  { value: "ON_UPDATE", label: "Updated (changes to an existing value)" },
  { value: "ON_CREATE_OR_UPDATE", label: "Created or updated" },
];

export function formatDataTableColumnTaskFireOn(
  fireOn: DataTableColumnTaskFireOn
): string {
  switch (fireOn) {
    case "ON_CREATE":
      return "created";
    case "ON_UPDATE":
      return "updated";
    case "ON_CREATE_OR_UPDATE":
      return "created or updated";
  }
}

export function serializeDataTableColumnTaskBindingConditions(
  filters: DataTableColumnFilter[]
): DataTableColumnTaskBindingCondition[] | null {
  const serialized = serializeDataTableRowFilters(filters);
  if (!serialized) return null;

  const parsed = JSON.parse(serialized) as Array<{
    column_id: string;
    operator: string;
    value: string | null;
    next_conjunction?: DataTableFilterConjunction;
  }>;

  return parsed.map((item) => ({
    column_id: item.column_id,
    operator: item.operator,
    value: item.value,
    next_conjunction: item.next_conjunction ?? null,
  }));
}

export function parseDataTableColumnTaskBindingConditions(
  conditions: DataTableColumnTaskBindingCondition[] | null | undefined
): DataTableColumnFilter[] {
  if (!conditions?.length) return [];

  return conditions.map((condition) => ({
    id: crypto.randomUUID(),
    column_id: condition.column_id,
    operator: condition.operator as DataTableColumnFilterOperator,
    value:
      condition.value === null || condition.value === undefined
        ? ""
        : condition.value,
    next_conjunction:
      (condition.next_conjunction as DataTableFilterConjunction | null) ??
      "AND",
  }));
}

export function formatDataTableColumnTaskBindingConditionSummary(
  conditions: DataTableColumnTaskBindingCondition[] | null | undefined,
  columns: DataTableColumnDefinition[]
): string | null {
  if (!conditions?.length) return null;

  const columnsById = new Map(columns.map((column) => [column.id, column]));

  return conditions
    .map((condition) => {
      const column = columnsById.get(condition.column_id);
      const operatorLabel =
        DATA_TABLE_FILTER_OPERATOR_LABELS[
          condition.operator as DataTableColumnFilterOperator
        ] ?? condition.operator;
      const valueSuffix =
        condition.value !== null &&
        condition.value !== undefined &&
        condition.value !== ""
          ? ` ${condition.value}`
          : "";
      return `${column?.display_name ?? "Column"} ${operatorLabel}${valueSuffix}`;
    })
    .join(" · ");
}

export function buildDataTableConnectedWorkerPath(
  dataTableId: string,
  agentKind: DataTableWorkerAgentKind,
  agentId: string
): string {
  return `/api/data-tables/${dataTableId}/connected-workers/${agentKind}/${agentId}`;
}

export function buildDataTableWorkerRunsPath(dataTableId: string): string {
  return `/api/data-tables/${dataTableId}/worker-runs`;
}

export function formatDataTableWorkerAgentKind(
  kind: DataTableWorkerAgentKind
): string {
  switch (kind) {
    case "SALES_CAMPAIGN":
      return "Sales Team";
    case "SUPPORT_LINE":
      return "Support Team";
    case "COMPUTER_USE":
      return "Computer Use Team";
    default:
      return kind
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
  }
}

export function formatDataTableWorkerChannel(
  channel: DataTableWorkerChannel
): string {
  switch (channel) {
    case "CALL":
      return "Voice call";
    case "SMS":
      return "SMS";
    case "EMAIL":
      return "Email";
    default:
      return String(channel);
  }
}

export function getDataTableConnectedWorkerKey(
  worker: DataTableConnectedWorker
): string {
  return `${worker.agent_kind}:${worker.agent_id}`;
}

export function getDataTableWorkerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
}

export function getDataTableWorkerAvatarClassName(
  channel: DataTableWorkerChannel
): string {
  switch (channel) {
    case "CALL":
      return "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300";
    case "SMS":
      return "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300";
    case "EMAIL":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
    default:
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  }
}

const DATA_TABLE_WORKER_AVATAR_PALETTE = [
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
] as const;

export function getDataTableWorkerAvatarClassNameByIndex(
  index: number
): string {
  return DATA_TABLE_WORKER_AVATAR_PALETTE[
    Math.abs(index) % DATA_TABLE_WORKER_AVATAR_PALETTE.length
  ];
}

export function getDataTableWorkerAvatarClassNameByKind(
  kind: DataTableWorkerAgentKind
): string {
  switch (kind) {
    case "SALES_CAMPAIGN":
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
    case "SUPPORT_LINE":
      return "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300";
    case "COMPUTER_USE":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
    default:
      return "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300";
  }
}

export function getDataTableWorkerChannelBadgeClassName(
  channel: DataTableWorkerChannel
): string {
  switch (channel) {
    case "CALL":
      return "bg-violet-600 text-white";
    case "SMS":
      return "bg-sky-600 text-white";
    case "EMAIL":
      return "bg-emerald-600 text-white";
    default:
      return "bg-amber-600 text-white";
  }
}

export function buildDataTableActivitiesPath(
  dataTableId: string,
  page = 1,
  limit = 30
): string {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  return `/api/data-tables/${dataTableId}/activities?${params.toString()}`;
}

export function cleanDataTableActivityDescription(
  description: string
): string {
  return description
    .replace(
      /\s*\(\s*created\s*=\s*[^,)]+\s*,\s*updated\s*=\s*[^,)]+\s*,\s*skipped\s*=\s*[^,)]+\s*,\s*failed\s*=\s*[^)]+\)\s*$/i,
      ""
    )
    .trim();
}

export function formatDataTableActivityTitle(
  activity: Pick<DataTableActivity, "activity_type" | "description" | "details">
): string {
  if (activity.activity_type === "CRM_PUSH") {
    const details = activity.details;
    const created =
      details && typeof details.created === "number" ? details.created : 0;
    const updated =
      details && typeof details.updated === "number" ? details.updated : 0;
    const skipped =
      details && typeof details.skipped === "number" ? details.skipped : 0;
    const failed =
      details && typeof details.failed === "number" ? details.failed : 0;
    let contactCount = created + updated + skipped + failed;

    if (contactCount === 0) {
      const match = cleanDataTableActivityDescription(
        activity.description
      ).match(/pushed\s+(\d+)/i);
      contactCount = match ? Number(match[1]) : 1;
    }

    return `Pushed ${contactCount} contact${contactCount === 1 ? "" : "s"} to CRM · Contacts`;
  }

  return cleanDataTableActivityDescription(activity.description);
}

export function formatDataTableActivityDetailsSummary(
  details: Record<string, unknown> | null | undefined
): string | null {
  if (!details || typeof details !== "object") return null;

  const parts: string[] = [];
  const created = details.created;
  const updated = details.updated;
  const skipped = details.skipped;
  const failed = details.failed;
  const rowCount = details.row_count ?? details.rows;

  if (typeof created === "number") parts.push(`${created} new`);
  if (typeof updated === "number") parts.push(`${updated} updated`);
  if (typeof skipped === "number") parts.push(`${skipped} skipped`);
  if (typeof failed === "number") parts.push(`${failed} failed`);
  if (parts.length === 0 && typeof rowCount === "number") {
    parts.push(`${rowCount} row${rowCount === 1 ? "" : "s"}`);
  }

  return parts.length > 0 ? parts.join(" · ") : null;
}

export const DATA_TABLE_CRM_DUPLICATE_POLICY = {
  SKIP: "SKIP",
  UPDATE: "UPDATE",
} as const satisfies Record<string, DataTableCrmDuplicatePolicy>;

export function normalizeDataTableCrmDuplicatePolicy(
  value: string | null | undefined
): DataTableCrmDuplicatePolicy {
  if (value === DATA_TABLE_CRM_DUPLICATE_POLICY.SKIP) {
    return DATA_TABLE_CRM_DUPLICATE_POLICY.SKIP;
  }
  return DATA_TABLE_CRM_DUPLICATE_POLICY.UPDATE;
}

export const DATA_TABLE_CRM_CONTACT_FIELDS: {
  key: DataTableCrmContactFieldKey;
  label: string;
  required?: boolean;
}[] = [
  { key: "fullname", label: "Full name", required: true },
  { key: "work_email", label: "Work email" },
  { key: "phone", label: "Phone" },
  { key: "company", label: "Company" },
  { key: "job_title", label: "Job title" },
  { key: "location", label: "Location" },
  { key: "linkedin", label: "LinkedIn" },
];

export function countDataTableCrmMappedFields(
  mapping: Record<DataTableCrmContactFieldKey, string | null>
): number {
  return DATA_TABLE_CRM_CONTACT_FIELDS.filter((field) =>
    Boolean(mapping[field.key])
  ).length;
}

export function buildDataTableCrmMappingPayload(
  mapping: Record<DataTableCrmContactFieldKey, string | null>
): DataTableCrmMappingFields {
  const payload: DataTableCrmMappingFields = {};
  DATA_TABLE_CRM_CONTACT_FIELDS.forEach((field) => {
    const columnId = mapping[field.key];
    if (columnId) {
      payload[field.key] = { column_id: columnId };
    }
  });
  return payload;
}

export function buildDataTableRowsImportPreviewPath(
  dataTableId: string
): string {
  return `/api/${dataTableId}/rows/import/preview`;
}

export function parseDataTableCsvHeaders(csvContent: string): string[] {
  const firstLine = csvContent.split(/\r?\n/)[0];
  if (!firstLine?.trim()) return [];
  return firstLine.split(",").map((header) => header.trim());
}

export function countDataTableCsvRows(csvContent: string): number {
  const lines = csvContent.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length <= 1) return 0;
  return lines.length - 1;
}

export function formatDataTableFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getAutoDataTableColumnMapping(
  columns: DataTableColumnDefinition[],
  csvColumns: string[]
): Record<string, string> {
  const mapping: Record<string, string> = {};

  columns.forEach((column) => {
    const match = csvColumns.find((header) => {
      const normalizedHeader = header.toLowerCase();
      return (
        normalizedHeader === column.display_name.toLowerCase() ||
        normalizedHeader === column.human_readable_id.toLowerCase()
      );
    });

    if (match) {
      mapping[column.id] = match;
    }
  });

  return mapping;
}

export function getAutoCsvColumnMapping(
  columns: DataTableColumnDefinition[],
  csvColumns: string[]
): Record<string, string> {
  const tableMapping = getAutoDataTableColumnMapping(columns, csvColumns);
  const csvMapping: Record<string, string> = {};

  csvColumns.forEach((csvColumn) => {
    csvMapping[csvColumn] = "";
  });

  Object.entries(tableMapping).forEach(([tableColumnId, csvColumn]) => {
    csvMapping[csvColumn] = tableColumnId;
  });

  return csvMapping;
}

export function buildTableColumnMappingFromCsvMapping(
  csvColumnMapping: Record<string, string>
): Record<string, string> {
  const mapping: Record<string, string> = {};

  Object.entries(csvColumnMapping).forEach(([csvColumn, tableColumnId]) => {
    if (tableColumnId) {
      mapping[tableColumnId] = csvColumn;
    }
  });

  return mapping;
}

export function getDataTableMappedColumnCount(
  csvColumnMapping: Record<string, string>
): number {
  return Object.values(csvColumnMapping).filter(Boolean).length;
}

export function areDataTableRequiredColumnsMapped(
  columns: DataTableColumnDefinition[],
  csvColumnMapping: Record<string, string>
): boolean {
  const mappedTableColumnIds = new Set(
    Object.values(csvColumnMapping).filter(Boolean)
  );

  return columns
    .filter((column) => column.is_required)
    .every((column) => mappedTableColumnIds.has(column.id));
}

export function buildDataTableSampleCsv(
  columns: DataTableColumnDefinition[]
): string {
  const headers = columns.map((column) => column.display_name).join(",");
  const sampleRow = columns
    .map((column) => {
      switch (column.data_type) {
        case "NUMBER":
          return "0";
        case "BOOLEAN":
          return "true";
        case "DATE":
          return "2024-01-01";
        case "TIME":
          return "10:00";
        case "TIMESTAMP":
          return "2024-01-01T10:00:00";
        case "CURRENCY":
          return "GBP";
        case "EMAIL":
          return "user@example.com";
        case "PHONE":
          return "+10000000000";
        case "URL":
          return "https://example.com";
        case "UUID":
          return "00000000-0000-0000-0000-000000000000";
        case "JSON":
          return "{}";
        case "SINGLE_SELECT":
          return getDataTableSingleSelectDefault(column) || "Option 1";
        default:
          return "Sample value";
      }
    })
    .join(",");

  return `${headers}\n${sampleRow}`;
}

export function downloadTextFile(
  content: string,
  filename: string,
  mimeType = "text/csv"
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export function buildDataTableBulkAddFormData(
  file: File,
  columnMappings: DataTableRowsBulkAddColumnMapping[]
): FormData {
  const formData = new FormData();
  formData.append("file", file);
  columnMappings.forEach((mapping) => {
    formData.append("column_mappings", JSON.stringify(mapping));
  });

  return formData;
}

const DATA_TABLE_TRUE_VALUES = new Set(["true", "1", "yes", "y"]);

export function parseDataTableBooleanValue(
  value: string | number | boolean | null | undefined
): boolean {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  return DATA_TABLE_TRUE_VALUES.has(value.trim().toLowerCase());
}

export function toDataTableBooleanString(
  value: string | number | boolean | null | undefined
): "true" | "false" {
  return parseDataTableBooleanValue(value) ? "true" : "false";
}

export function getDataTableSingleSelectOptions(
  column: DataTableColumnDefinition
): string[] {
  if (column.data_type !== "SINGLE_SELECT") {
    return [];
  }

  return (column.column_options?.options ?? [])
    .map((option) => {
      if (typeof option === "string") {
        return option.trim();
      }
      return option.value?.trim() ?? "";
    })
    .filter(Boolean);
}

export function getDataTableSingleSelectOptionColorMap(
  column?: Pick<DataTableColumnDefinition, "column_options"> | null
): Record<string, string> {
  const map: Record<string, string> = {};
  const rawOptions = column?.column_options?.options ?? [];

  rawOptions.forEach((option) => {
    if (typeof option === "string") return;
    const value = option.value?.trim();
    if (value && option.color) {
      map[value] = singleSelectColorToHex(option.color);
    }
  });

  const legacyColors = column?.column_options?.colors ?? {};
  Object.entries(legacyColors).forEach(([label, token]) => {
    const value = label.trim();
    if (value && token && !map[value]) {
      map[value] = singleSelectColorToHex(token);
    }
  });

  return map;
}

export function getDataTableSingleSelectOptionHex(
  option: string,
  column?: Pick<DataTableColumnDefinition, "column_options"> | null
): string | null {
  return getDataTableSingleSelectOptionColorMap(column)[option] ?? null;
}

export function resolveDataTableSingleSelectColorToken(
  option: string,
  column?: Pick<DataTableColumnDefinition, "column_options"> | null
): DataTableSingleSelectColor | null {
  const hex = getDataTableSingleSelectOptionHex(option, column);
  if (hex) {
    return resolveSingleSelectColorFromHex(hex);
  }

  return column?.column_options?.colors?.[option] ?? null;
}

const DATA_TABLE_PRIMARY_KEY_TYPES: DataTableColumnDataType[] = [
  "TEXT",
  "NUMBER",
  "EMAIL",
  "PHONE",
  "URL",
];

export function canBeDataTablePrimaryKey(
  dataType: DataTableColumnDataType
): boolean {
  return DATA_TABLE_PRIMARY_KEY_TYPES.includes(dataType);
}

export function getDataTablePrimaryKeyColumn(
  columns: DataTableColumnDefinition[]
): DataTableColumnDefinition | null {
  return columns.find((column) => column.is_primary) ?? null;
}

export function getDataTablePrimaryKeyToast(
  columnName: string,
  isPrimary: boolean
): { title: string; description: string } {
  return {
    title: isPrimary
      ? `“${columnName}” is now the primary key`
      : `“${columnName}” is no longer the primary key`,
    description: isPrimary
      ? "Agents and imports will match rows on this column."
      : "This table has no primary key until you choose another column.",
  };
}

export function getDataTableSingleSelectDefault(
  column: DataTableColumnDefinition
): string {
  const options = getDataTableSingleSelectOptions(column);
  const defaultValue = column.column_options?.default?.trim() ?? "";

  if (defaultValue && options.includes(defaultValue)) {
    return defaultValue;
  }

  return options[0] ?? "";
}

const SINGLE_SELECT_PILL_STYLES = [
  {
    pill: "border-blue-200 bg-blue-100 text-blue-800 dark:border-blue-800/50 dark:bg-blue-950/50 dark:text-blue-300",
    dot: "bg-blue-800 dark:bg-blue-300",
  },
  {
    pill: "border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-950/50 dark:text-emerald-300",
    dot: "bg-emerald-800 dark:bg-emerald-300",
  },
  {
    pill: "border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/50 dark:text-amber-300",
    dot: "bg-amber-900 dark:bg-amber-300",
  },
  {
    pill: "border-rose-200 bg-rose-100 text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/50 dark:text-rose-300",
    dot: "bg-rose-800 dark:bg-rose-300",
  },
  {
    pill: "border-violet-200 bg-violet-100 text-violet-800 dark:border-violet-800/50 dark:bg-violet-950/50 dark:text-violet-300",
    dot: "bg-violet-800 dark:bg-violet-300",
  },
  {
    pill: "border-cyan-200 bg-cyan-100 text-cyan-800 dark:border-cyan-800/50 dark:bg-cyan-950/50 dark:text-cyan-300",
    dot: "bg-cyan-800 dark:bg-cyan-300",
  },
  {
    pill: "border-orange-200 bg-orange-100 text-orange-900 dark:border-orange-800/50 dark:bg-orange-950/50 dark:text-orange-300",
    dot: "bg-orange-900 dark:bg-orange-300",
  },
  {
    pill: "border-pink-200 bg-pink-100 text-pink-800 dark:border-pink-800/50 dark:bg-pink-950/50 dark:text-pink-300",
    dot: "bg-pink-800 dark:bg-pink-300",
  },
  {
    pill: "border-teal-200 bg-teal-100 text-teal-800 dark:border-teal-800/50 dark:bg-teal-950/50 dark:text-teal-300",
    dot: "bg-teal-800 dark:bg-teal-300",
  },
  {
    pill: "border-indigo-200 bg-indigo-100 text-indigo-800 dark:border-indigo-800/50 dark:bg-indigo-950/50 dark:text-indigo-300",
    dot: "bg-indigo-800 dark:bg-indigo-300",
  },
] as const;

function getDataTableSingleSelectStyleIndex(option: string): number {
  let hash = 0;
  for (let index = 0; index < option.length; index += 1) {
    hash = (hash * 31 + option.charCodeAt(index)) >>> 0;
  }
  return hash % SINGLE_SELECT_PILL_STYLES.length;
}

export function getDataTableSingleSelectPillClassName(
  option: string,
  column?: Pick<DataTableColumnDefinition, "column_options"> | null
): string {
  const token = resolveDataTableSingleSelectColorToken(option, column);
  if (token) {
    return getStatusColor(token).chipClass;
  }

  return SINGLE_SELECT_PILL_STYLES[getDataTableSingleSelectStyleIndex(option)]
    .pill;
}

export function getDataTableSingleSelectDotClassName(
  option: string,
  column?: Pick<DataTableColumnDefinition, "column_options"> | null
): string {
  const token = resolveDataTableSingleSelectColorToken(option, column);
  if (token) {
    return getStatusColor(token).dotClass;
  }

  return SINGLE_SELECT_PILL_STYLES[getDataTableSingleSelectStyleIndex(option)]
    .dot;
}

export function parseDataTableSingleSelectOptionDrafts(
  column?: DataTableColumnDefinition | null
): DataTableSingleSelectOptionDraft[] {
  const colorMap = getDataTableSingleSelectOptionColorMap(column);
  const options = column ? getDataTableSingleSelectOptions(column) : [];

  return options.map((label, index) =>
    createSingleSelectOptionDraft(
      label,
      colorMap[label]
        ? resolveSingleSelectColorFromHex(colorMap[label])
        : nextSingleSelectColor(
            options
              .slice(0, index)
              .map((option) =>
                colorMap[option]
                  ? resolveSingleSelectColorFromHex(colorMap[option])
                  : null
              )
              .filter(Boolean) as DataTableSingleSelectColor[]
          )
    )
  );
}

export function buildDataTableSingleSelectColumnOptions(params: {
  options: string[];
  defaultValue?: string;
  colors?: Partial<Record<string, DataTableSingleSelectColor>>;
}): DataTableSingleSelectColumnOptions {
  const options = params.options.map((option) => option.trim()).filter(Boolean);
  const defaultValue = params.defaultValue?.trim() ?? "";
  const colorEntries = params.colors ?? {};

  return {
    options: options.map((value) => ({
      value,
      color: singleSelectColorToHex(colorEntries[value] ?? "blue"),
    })),
    default:
      defaultValue && options.includes(defaultValue)
        ? defaultValue
        : options[0] ?? null,
  };
}

export function buildDataTableSingleSelectColumnOptionsFromDrafts(
  drafts: DataTableSingleSelectOptionDraft[],
  defaultValue?: string
): DataTableSingleSelectColumnOptions {
  const options = drafts.map((draft) => draft.label.trim()).filter(Boolean);
  const colors: Partial<Record<string, DataTableSingleSelectColor>> = {};

  drafts.forEach((draft) => {
    const label = draft.label.trim();
    if (label) {
      colors[label] = draft.color;
    }
  });

  return buildDataTableSingleSelectColumnOptions({
    options,
    defaultValue,
    colors,
  });
}

export function formatDataTableCellValue(
  value: string | number | boolean | null | undefined
): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
}

export function getDataTableUrlHref(value: string): string | null {
  const trimmedValue = value.trim();
  if (!trimmedValue || trimmedValue === "—") return null;
  if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
  if (/^[a-z][a-z\d+.-]*:/i.test(trimmedValue)) return null;
  return `https://${trimmedValue}`;
}

export function buildRowValuesPayload(
  columns: DataTableColumnDefinition[],
  values: Record<string, string>
): DataTableRowValuePayload[] {
  return columns.map((column) => {
    const raw = values[column.id] ?? "";

    if (column.data_type === "BOOLEAN") {
      return {
        column_id: column.id,
        value: toDataTableBooleanString(raw),
      };
    }

    return {
      column_id: column.id,
      value: raw.trim() === "" ? null : raw,
    };
  });
}

export function getInitialRowValues(
  columns: DataTableColumnDefinition[],
  row?: DataTableRow | null
): Record<string, string> {
  const initialValues: Record<string, string> = {};

  columns.forEach((column) => {
    const cell = row?.cells.find((item) => item.column_id === column.id);
    const cellValue = cell?.value;

    if (cellValue === null || cellValue === undefined) {
      if (column.data_type === "BOOLEAN") {
        initialValues[column.id] = "false";
        return;
      }

      if (column.data_type === "SINGLE_SELECT" && !row) {
        initialValues[column.id] = getDataTableSingleSelectDefault(column);
        return;
      }

      initialValues[column.id] = "";
      return;
    }

    if (column.data_type === "BOOLEAN") {
      initialValues[column.id] = toDataTableBooleanString(cellValue);
      return;
    }

    if (typeof cellValue === "boolean") {
      initialValues[column.id] = cellValue ? "true" : "false";
      return;
    }

    initialValues[column.id] = String(cellValue);
  });

  return initialValues;
}

export function getDataTableRowTitle(
  row: DataTableRow,
  columns: DataTableColumnDefinition[]
): string {
  const preferred =
    columns.find((column) => column.data_type === "TEXT") ?? columns[0];

  if (!preferred) {
    return "Row";
  }

  const cell = row.cells.find((item) => item.column_id === preferred.id);
  const value = cell?.value;

  if (value === null || value === undefined || String(value).trim() === "") {
    return preferred.display_name;
  }

  return String(value);
}

const DATA_TABLE_CONFIGURATION_FORBIDDEN_CODE = "DATA_TABLE__FORBIDDEN";
const DATA_TABLE_CONFIGURATION_FORBIDDEN_MESSAGE =
  "You do not have permission to edit the table configuration";

function getDataTableErrorData(error: unknown):
  | {
      message?: string;
      error_code?: string;
      detail?: string | Array<{ msg?: string; message?: string }>;
    }
  | undefined {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return undefined;
  }

  return (
    error as {
      response?: {
        data?: {
          message?: string;
          error_code?: string;
          detail?: string | Array<{ msg?: string; message?: string }>;
        };
      };
    }
  ).response?.data;
}

export function getDataTableApiErrorMessage(error: unknown): string {
  const data = getDataTableErrorData(error);
  if (data?.message) {
    return data.message;
  }
  if (Array.isArray(data?.detail)) {
    return data.detail
      .map((item) => item.msg ?? item.message ?? "Validation error")
      .join(" ");
  }
  if (typeof data?.detail === "string") {
    return data.detail;
  }
  if (data?.error_code) {
    return `Error: ${data.error_code}`;
  }
  return "An unexpected error occurred";
}

export function isDataTableConfigurationForbidden(error: unknown): boolean {
  const data = getDataTableErrorData(error);
  const code = String(data?.error_code ?? "").toUpperCase();
  if (code === DATA_TABLE_CONFIGURATION_FORBIDDEN_CODE) {
    return true;
  }

  const message = String(data?.message ?? "").toLowerCase();
  return message.includes("read-only access to this data table");
}

export function getDataTableConfigurationErrorMessage(error: unknown): string {
  if (isDataTableConfigurationForbidden(error)) {
    return DATA_TABLE_CONFIGURATION_FORBIDDEN_MESSAGE;
  }
  return getDataTableApiErrorMessage(error);
}

export function reorderDataTableColumnsInMemory(
  columns: DataTableColumnDefinition[],
  sourceIndex: number,
  destinationIndex: number
): DataTableColumnDefinition[] {
  const next = [...columns];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(destinationIndex, 0, moved);
  return next;
}

export function getDataTableColumnReorderDisplayOrder(
  originalOrder: DataTableColumnDefinition[],
  reorderedColumns: DataTableColumnDefinition[],
  movedColumn: DataTableColumnDefinition
): number {
  const sourceIndex = originalOrder.findIndex(
    (column) => column.id === movedColumn.id
  );
  const nextIndex = reorderedColumns.findIndex(
    (column) => column.id === movedColumn.id
  );

  if (sourceIndex === -1 || nextIndex === -1 || sourceIndex === nextIndex) {
    return movedColumn.display_order;
  }

  if (sourceIndex < nextIndex) {
    const displacedColumn = originalOrder[nextIndex];
    if (
      displacedColumn &&
      displacedColumn.id !== movedColumn.id &&
      displacedColumn.display_order !== movedColumn.display_order
    ) {
      return displacedColumn.display_order;
    }

    const nextColumn = originalOrder[nextIndex + 1];
    if (nextColumn && nextColumn.id !== movedColumn.id) {
      return nextColumn.display_order;
    }
  }

  return (
    originalOrder[nextIndex]?.display_order ?? movedColumn.display_order
  );
}

export function formatDataTableDate(dateString: string): string {
  return format(new Date(dateString), "MMM d, yyyy");
}

export function getDefaultDataTableSortState(defaultSort: {
  sortBy: DataTableSortField;
  sortOrder: "asc" | "desc";
}): DataTableSortState {
  return {
    sortBy: defaultSort.sortBy,
    sortOrder: defaultSort.sortOrder,
    activeSortColumn: null,
  };
}

export function getNextDataTableSortState(
  current: DataTableSortState,
  columnKey: DataTableSortField,
  defaultSort: { sortBy: DataTableSortField; sortOrder: "asc" | "desc" }
): DataTableSortState {
  if (current.activeSortColumn !== columnKey) {
    return {
      sortBy: columnKey,
      sortOrder: "asc",
      activeSortColumn: columnKey,
    };
  }

  if (current.sortOrder === "asc") {
    return {
      ...current,
      sortOrder: "desc",
    };
  }

  return getDefaultDataTableSortState(defaultSort);
}
